import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCompanyById, getPreparerById, saveIRSWaiverSubmission, createMobileSignatureSession, subscribeToMobileSignatureSession, deleteMobileSignatureSession, getMobileSignatureSession } from '../firestoreHelper';
import { QRCodeSVG } from 'qrcode.react';
import { toEasternDateString, toEasternInputDate, toEasternISOString, getEasternYear } from '../dateHelper';

// Helper to compress image to reduce size for Firestore storage
const compressImage = (dataUrl, maxWidth = 600, quality = 0.6) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Scale down if needed
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to compressed JPEG
      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = () => resolve(dataUrl); // Return original if error
    img.src = dataUrl;
  });
};

/**
 * IRS Waiver Intake Form - Complete 6-page form for clients
 * Matches the Friends Tax Services LLC PDF template
 */
function IRSWaiverIntakeForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [company, setCompany] = useState(null);
  const [preparer, setPreparer] = useState(null);
  
  // Current step: 'info' -> 'dependents' -> 'waiver' -> 'engagement' -> 'photos' -> 'review'
  const [currentStep, setCurrentStep] = useState('info');
  
  // Form data - Use taxYear from URL if provided, otherwise use current year
  const urlTaxYearParam = new URLSearchParams(window.location.search).get('taxYear');
  const [formData, setFormData] = useState({
    // Tax Year - Use from URL if provided, otherwise current year
    taxYear: urlTaxYearParam ? parseInt(urlTaxYearParam) : getEasternYear(),
    
    // Page 1 - IRS Notice 2014-7
    medicaidElection: '', // 'exclude' or 'include'
    
    // Page 2 - Care Recipient Info (To Whom It May Concern)
    careRecipientName: '',
    careRecipientAddress: '',
    
    // Page 3 - Taxpayer Info
    taxpayerFirstName: '',
    taxpayerMiddleInitial: '',
    taxpayerLastName: '',
    taxpayerOccupation: '',
    taxpayerEmail: '',
    taxpayerDOB: '',
    taxpayerSSN: '',
    taxpayerPhone: '',
    taxpayerAddress: '',
    taxpayerCity: '',
    taxpayerState: '',
    taxpayerZipCode: '',
    
    // Spouse Info
    spouseFirstName: '',
    spouseMiddleInitial: '',
    spouseLastName: '',
    spouseOccupation: '',
    spouseEmail: '',
    spouseDOB: '',
    spouseSSN: '',
    spousePhone: '',
    
    // Dependents (up to 4)
    dependents: [
      { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
      { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
      { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
      { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' }
    ],
    
    // Page 4-5 - Engagement Letter Acceptance
    engagementAccepted: false,
    
    // Signatures
    taxpayerSignatureDate: '',
    spouseSignatureDate: ''
  });
  
  // Photo ID uploads
  // eslint-disable-next-line no-unused-vars
  const [consumerPhotoID, setConsumerPhotoID] = useState(null);
  const [consumerPhotoPreview, setConsumerPhotoPreview] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [caregiverPhotoID, setCaregiverPhotoID] = useState(null);
  const [caregiverPhotoPreview, setCaregiverPhotoPreview] = useState(null);
  
  // Signatures
  const [taxpayerSignature, setTaxpayerSignature] = useState('');
  const [spouseSignature, setSpouseSignature] = useState('');
  const [signatureType, setSignatureType] = useState('draw');
  const [activeSignature, setActiveSignature] = useState('taxpayer'); // 'taxpayer' or 'spouse'
  const canvasRef = useRef(null);
  const spouseCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Pending signatures from QR code (need Accept/Decline)
  const [pendingTaxpayerSignature, setPendingTaxpayerSignature] = useState(null);
  const [pendingSpouseSignature, setPendingSpouseSignature] = useState(null);
  
  // QR Code Mobile Signature states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrSignatureType, setQrSignatureType] = useState('taxpayer'); // which signature the QR is for
  const [qrLoading, setQrLoading] = useState(false);
  const [qrReceivedSignature, setQrReceivedSignature] = useState(null); // Signature received in QR modal
  const qrUnsubscribeRef = useRef(null);
  const qrPollIntervalRef = useRef(null); // Polling fallback interval

  const companyId = searchParams.get('company');
  const preparerId = searchParams.get('preparer');
  // eslint-disable-next-line no-unused-vars
  const urlTaxYear = searchParams.get('taxYear');
  
  // Helper function to handle signature received (used by both real-time and polling)
  const handleSignatureReceived = (signature, sessionId) => {
    console.log('🎯 handleSignatureReceived called, setting state...');
    setQrReceivedSignature(signature);
    
    // Clean up subscription
    if (qrUnsubscribeRef.current) {
      qrUnsubscribeRef.current();
      qrUnsubscribeRef.current = null;
    }
    
    // Clean up polling
    if (qrPollIntervalRef.current) {
      clearInterval(qrPollIntervalRef.current);
      qrPollIntervalRef.current = null;
    }
    
    // Delete session after a short delay
    setTimeout(() => {
      deleteMobileSignatureSession(sessionId);
    }, 500);
  };

  // Load company and preparer info
  useEffect(() => {
    const loadData = async () => {
      try {
        if (companyId) {
          const companyData = await getCompanyById(companyId);
          setCompany(companyData);
        }
        if (preparerId) {
          const preparerData = await getPreparerById(preparerId);
          setPreparer(preparerData);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [companyId, preparerId]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle dependent changes
  const handleDependentChange = (index, field, value) => {
    setFormData(prev => {
      const newDependents = [...prev.dependents];
      newDependents[index] = { ...newDependents[index], [field]: value };
      return { ...prev, dependents: newDependents };
    });
  };

  // Format phone number
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  // Format date of birth (MM/DD/YYYY) for text input
  const formatDOB = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 8);
    if (limited.length <= 2) return limited;
    if (limited.length <= 4) return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  };

  // Convert MM/DD/YYYY to YYYY-MM-DD for date input
  const toDateInputFormat = (mmddyyyy) => {
    if (!mmddyyyy || mmddyyyy.length !== 10) return '';
    const parts = mmddyyyy.split('/');
    if (parts.length !== 3) return '';
    return `${parts[2]}-${parts[0]}-${parts[1]}`;
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const toDisplayDateFormat = (yyyymmdd) => {
    if (!yyyymmdd || yyyymmdd.length !== 10) return '';
    const parts = yyyymmdd.split('-');
    if (parts.length !== 3) return '';
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  };

  // Handle DOB text input change
  const handleDOBTextChange = (name, value) => {
    const formatted = formatDOB(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  // Handle DOB date picker change
  const handleDOBDatePickerChange = (name, value) => {
    const displayFormat = toDisplayDateFormat(value);
    setFormData(prev => ({ ...prev, [name]: displayFormat }));
  };

  // Handle dependent DOB text change
  const handleDependentDOBTextChange = (index, value) => {
    const formatted = formatDOB(value);
    handleDependentChange(index, 'dob', formatted);
  };

  // Handle dependent DOB date picker change
  const handleDependentDOBDatePickerChange = (index, value) => {
    const displayFormat = toDisplayDateFormat(value);
    handleDependentChange(index, 'dob', displayFormat);
  };

  // Format SSN (show last 4 only for display)
  const formatSSN = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 9);
    if (limited.length <= 3) return limited;
    if (limited.length <= 5) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
  };

  // Handle photo uploads
  const handlePhotoUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'consumer') {
          setConsumerPhotoID(file);
          setConsumerPhotoPreview(reader.result);
        } else {
          setCaregiverPhotoID(file);
          setCaregiverPhotoPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Canvas signature functions
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = activeSignature === 'spouse' ? spouseCanvasRef.current : canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = activeSignature === 'spouse' ? spouseCanvasRef.current : canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    const canvas = activeSignature === 'spouse' ? spouseCanvasRef.current : canvasRef.current;
    if (isDrawing && canvas) {
      const signatureData = canvas.toDataURL();
      if (activeSignature === 'taxpayer') {
        setTaxpayerSignature(signatureData);
      } else {
        setSpouseSignature(signatureData);
      }
    }
    setIsDrawing(false);
  };

  const clearSignature = (type = null) => {
    const signatureTarget = type || activeSignature;
    const canvas = signatureTarget === 'spouse' ? spouseCanvasRef.current : canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (signatureTarget === 'taxpayer') {
      setTaxpayerSignature('');
    } else {
      setSpouseSignature('');
    }
  };

  // QR Code Mobile Signature Functions
  const generateQRCode = async (sigType) => {
    console.log('generateQRCode called with:', sigType);
    setQrLoading(true);
    setQrSignatureType(sigType);
    
    try {
      // Determine the name based on signature type
      const signerName = sigType === 'spouse' 
        ? `${formData.spouseFirstName} ${formData.spouseLastName}`.trim() || 'Spouse'
        : `${formData.taxpayerFirstName} ${formData.taxpayerLastName}`.trim() || 'Client';
      
      console.log('Creating session for:', signerName);
      
      // Create a new session in Firestore
      const session = await createMobileSignatureSession({
        signatureType: sigType,
        companyId: companyId || '',
        preparerId: preparerId || '',
        companyName: company?.name || '',
        taxpayerName: sigType === 'taxpayer' ? signerName : '',
        spouseName: sigType === 'spouse' ? signerName : '',
        signerName: signerName
      });
      
      console.log('Session created:', session);
      
      setQrSessionId(session.sessionId);
      setShowQRModal(true);
      setQrLoading(false);
      setQrReceivedSignature(null); // Reset any previous signature
      
      // Subscribe to real-time updates for this session
      const unsubscribe = subscribeToMobileSignatureSession(session.sessionId, (sessionData) => {
        console.log('📱 Subscription callback received:', JSON.stringify(sessionData ? { status: sessionData.status, hasSignature: !!sessionData.signature } : null));
        
        if (sessionData && sessionData.status === 'signed' && sessionData.signature) {
          console.log('✅ Signature received via real-time! Length:', sessionData.signature.length);
          handleSignatureReceived(sessionData.signature, session.sessionId);
        } else if (sessionData) {
          console.log('⏳ Session exists but not signed yet. Status:', sessionData.status);
        }
      });
      
      qrUnsubscribeRef.current = unsubscribe;
      
      // POLLING: Check every 1.5 seconds for signature (more reliable than real-time listener)
      console.log('🚀 Starting automatic polling for session:', session.sessionId);
      const sessionIdCopy = session.sessionId; // Capture for closure
      
      const pollInterval = setInterval(async () => {
        try {
          const sessionData = await getMobileSignatureSession(sessionIdCopy);
          if (sessionData && sessionData.status === 'signed' && sessionData.signature) {
            console.log('✅ Signature found via automatic polling!');
            clearInterval(pollInterval);
            qrPollIntervalRef.current = null;
            handleSignatureReceived(sessionData.signature, sessionIdCopy);
          }
        } catch (err) {
          // Don't stop polling on transient errors, just log
          console.log('⚠️ Polling error (will retry):', err.message);
        }
      }, 1500); // Check every 1.5 seconds
      
      // Store poll interval ref to clean up later
      qrPollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    }
    
    setQrLoading(false);
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrReceivedSignature(null);
    
    // Clean up the session
    if (qrSessionId) {
      deleteMobileSignatureSession(qrSessionId);
    }
    
    // Unsubscribe from real-time updates
    if (qrUnsubscribeRef.current) {
      qrUnsubscribeRef.current();
      qrUnsubscribeRef.current = null;
    }
    
    // Clean up polling interval
    if (qrPollIntervalRef.current) {
      clearInterval(qrPollIntervalRef.current);
      qrPollIntervalRef.current = null;
    }
    
    setQrSessionId(null);
  };

  // Manual check for signature - fallback when real-time/polling fails
  const manualCheckSignature = async () => {
    if (!qrSessionId) return;
    
    try {
      console.log('🔍 Manual check triggered for session:', qrSessionId);
      const sessionData = await getMobileSignatureSession(qrSessionId);
      console.log('🔍 Manual check result:', sessionData ? { status: sessionData.status, hasSignature: !!sessionData.signature } : 'null');
      
      if (sessionData && sessionData.status === 'signed' && sessionData.signature) {
        console.log('✅ Signature found via manual check!');
        handleSignatureReceived(sessionData.signature, qrSessionId);
      } else if (sessionData) {
        alert('Signature not received yet. Please make sure you signed on your phone and tapped Submit.');
      } else {
        alert('Session expired or not found. Please try again.');
      }
    } catch (err) {
      console.error('❌ Manual check error:', err);
      alert('Error checking for signature. Please try again.');
    }
  };

  // Accept signature from QR modal - sets it directly as the signature
  const acceptQrSignature = () => {
    if (qrReceivedSignature) {
      if (qrSignatureType === 'taxpayer') {
        setTaxpayerSignature(qrReceivedSignature);
      } else {
        setSpouseSignature(qrReceivedSignature);
      }
      setQrReceivedSignature(null);
      setShowQRModal(false);
      setQrSessionId(null);
    }
  };

  // Decline signature from QR modal - allow re-scanning
  const declineQrSignature = () => {
    setQrReceivedSignature(null);
    // Generate a new QR code for re-signing
    generateQRCode(qrSignatureType);
  };

  // Accept pending signature from QR code (legacy - for old pending flow)
  const acceptPendingSignature = (type) => {
    if (type === 'taxpayer' && pendingTaxpayerSignature) {
      setTaxpayerSignature(pendingTaxpayerSignature);
      setPendingTaxpayerSignature(null);
    } else if (type === 'spouse' && pendingSpouseSignature) {
      setSpouseSignature(pendingSpouseSignature);
      setPendingSpouseSignature(null);
    }
  };

  // Decline pending signature - allow re-signing
  const declinePendingSignature = (type) => {
    if (type === 'taxpayer') {
      setPendingTaxpayerSignature(null);
    } else {
      setPendingSpouseSignature(null);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (qrUnsubscribeRef.current) {
        qrUnsubscribeRef.current();
      }
      if (qrPollIntervalRef.current) {
        clearInterval(qrPollIntervalRef.current);
      }
      if (qrSessionId) {
        deleteMobileSignatureSession(qrSessionId);
      }
    };
  }, [qrSessionId]);

  // Navigation
  const steps = ['info', 'dependents', 'waiver', 'engagement', 'photos', 'review'];
  const stepLabels = {
    info: 'Client Information',
    dependents: 'Dependents',
    waiver: 'IRS Waiver Election',
    engagement: 'Engagement Letter',
    photos: 'Photo ID Upload',
    review: 'Review & Sign'
  };

  const nextStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
      window.scrollTo(0, 0);
    }
  };

  // Submit form
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Compress photo IDs if they exist (to reduce Firestore document size)
      let compressedConsumerPhoto = '';
      let compressedCaregiverPhoto = '';
      
      if (consumerPhotoPreview) {
        try {
          compressedConsumerPhoto = await compressImage(consumerPhotoPreview, 400, 0.4);
        } catch (e) {
          // Use original but truncated if too large (max ~500KB for Firestore)
          compressedConsumerPhoto = consumerPhotoPreview.length < 500000 ? consumerPhotoPreview : '';
        }
      }
      
      if (caregiverPhotoPreview) {
        try {
          compressedCaregiverPhoto = await compressImage(caregiverPhotoPreview, 400, 0.4);
        } catch (e) {
          compressedCaregiverPhoto = caregiverPhotoPreview.length < 500000 ? caregiverPhotoPreview : '';
        }
      }
      
      // Prepare consent data for PDF generation and Firebase storage
      const consentData = {
        taxYear: formData.taxYear,
        taxpayerInfo: {
          name: `${formData.taxpayerFirstName} ${formData.taxpayerMiddleInitial} ${formData.taxpayerLastName}`.trim(),
          firstName: formData.taxpayerFirstName,
          middleInitial: formData.taxpayerMiddleInitial,
          lastName: formData.taxpayerLastName,
          email: formData.taxpayerEmail,
          phone: formData.taxpayerPhone,
          address: formData.taxpayerAddress,
          city: formData.taxpayerCity,
          state: formData.taxpayerState,
          zipCode: formData.taxpayerZipCode,
          ssn: formData.taxpayerSSN?.slice(-4) || '',
          fullSSN: formData.taxpayerSSN || '', // Store full SSN for verification
          dob: formData.taxpayerDOB,
          occupation: formData.taxpayerOccupation
        },
        spouseInfo: {
          name: `${formData.spouseFirstName} ${formData.spouseMiddleInitial} ${formData.spouseLastName}`.trim(),
          firstName: formData.spouseFirstName,
          middleInitial: formData.spouseMiddleInitial,
          lastName: formData.spouseLastName,
          email: formData.spouseEmail,
          phone: formData.spousePhone,
          ssn: formData.spouseSSN?.slice(-4) || '',
          fullSSN: formData.spouseSSN || '',
          dob: formData.spouseDOB,
          occupation: formData.spouseOccupation
        },
        dependents: formData.dependents.filter(d => d.firstName || d.lastName),
        careRecipient: {
          name: formData.careRecipientName,
          address: formData.careRecipientAddress
        },
        election: formData.medicaidElection,
        consents: {
          medicaidElection: formData.medicaidElection,
          engagementAccepted: formData.engagementAccepted
        },
        signature: {
          // Limit signature size to avoid Firestore limits (max 1MB per document)
          image: taxpayerSignature && taxpayerSignature.length < 500000 ? taxpayerSignature : (taxpayerSignature ? 'SIGNATURE_TOO_LARGE' : ''),
          hasSignature: !!taxpayerSignature,
          date: formData.taxpayerSignatureDate || toEasternDateString()
        },
        spouseSignature: {
          image: spouseSignature && spouseSignature.length < 500000 ? spouseSignature : (spouseSignature ? 'SIGNATURE_TOO_LARGE' : ''),
          hasSignature: !!spouseSignature,
          date: formData.spouseSignatureDate || ''
        },
        photoIDs: {
          // Store compressed images for verification
          consumer: compressedConsumerPhoto || '',
          caregiver: compressedCaregiverPhoto || '',
          consumerUploaded: !!consumerPhotoPreview,
          caregiverUploaded: !!caregiverPhotoPreview
        },
        // Metadata
        companyId: companyId || '',
        preparerId: preparerId || '',
        companyName: company?.name || '',
        preparerName: preparer?.name || '',
        status: 'pending_verification', // Pending until preparer verifies
        submittedAt: toEasternISOString()
      };

      // Save to Firebase for preparer verification
      try {
        await saveIRSWaiverSubmission(consentData);
      } catch (saveErr) {
        // Continue anyway - form will still generate PDF
      }
      
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Error submitting form. Please try again.');
    }
    setSubmitting(false);
  };

  // Check if mobile device
  const isMobile = window.innerWidth <= 768;

  // Styles - Mobile-friendly and clean
  const styles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '10px' : '20px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      minHeight: '100vh',
      background: '#f8fafc'
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? '15px' : '30px',
      padding: isMobile ? '15px' : '25px',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      borderRadius: isMobile ? '8px' : '12px',
      color: 'white',
      boxShadow: '0 4px 15px rgba(30, 58, 138, 0.3)'
    },
    logo: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      marginBottom: '10px',
      borderRadius: '50%',
      objectFit: 'cover'
    },
    companyName: {
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: 'bold',
      marginBottom: '5px',
      letterSpacing: '0.5px'
    },
    companyInfo: {
      fontSize: isMobile ? '12px' : '14px',
      opacity: 0.9,
      marginBottom: '3px'
    },
    progressBar: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: isMobile ? '15px' : '30px',
      padding: isMobile ? '0 5px' : '0 10px',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch'
    },
    progressStep: {
      flex: isMobile ? '0 0 auto' : 1,
      textAlign: 'center',
      position: 'relative',
      minWidth: isMobile ? '50px' : 'auto',
      padding: isMobile ? '0 5px' : '0'
    },
    progressDot: (active, completed) => ({
      width: isMobile ? '24px' : '30px',
      height: isMobile ? '24px' : '30px',
      borderRadius: '50%',
      background: completed ? '#22c55e' : active ? '#3b82f6' : '#e5e7eb',
      color: completed || active ? 'white' : '#6b7280',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 8px',
      fontWeight: 'bold',
      fontSize: isMobile ? '11px' : '14px',
      boxShadow: active ? '0 2px 8px rgba(59, 130, 246, 0.4)' : 'none',
      transition: 'all 0.3s ease'
    }),
    progressLabel: (active) => ({
      fontSize: isMobile ? '9px' : '12px',
      color: active ? '#1e3a8a' : '#6b7280',
      fontWeight: active ? '600' : '400',
      whiteSpace: isMobile ? 'nowrap' : 'normal'
    }),
    section: {
      background: 'white',
      borderRadius: isMobile ? '8px' : '12px',
      padding: isMobile ? '15px' : '25px',
      marginBottom: isMobile ? '12px' : '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      color: '#1e3a8a',
      marginBottom: isMobile ? '15px' : '20px',
      paddingBottom: '10px',
      borderBottom: '2px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: isMobile ? '12px' : '15px',
      marginBottom: isMobile ? '12px' : '15px'
    },
    formGroup: {
      marginBottom: isMobile ? '12px' : '15px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '500',
      color: '#374151',
      fontSize: isMobile ? '13px' : '14px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '12px' : '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: isMobile ? '16px' : '14px', // 16px prevents zoom on iOS
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none'
    },
    select: {
      width: '100%',
      padding: isMobile ? '12px' : '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: isMobile ? '16px' : '14px',
      background: 'white',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      paddingRight: '35px'
    },
    checkbox: {
      marginRight: '10px',
      width: isMobile ? '22px' : '18px',
      height: isMobile ? '22px' : '18px',
      accentColor: '#3b82f6',
      cursor: 'pointer'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      cursor: 'pointer',
      padding: isMobile ? '12px' : '10px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      marginBottom: '10px',
      background: 'white',
      transition: 'all 0.2s'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '12px' : '10px'
    },
    radioLabel: (selected) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: isMobile ? '16px' : '15px',
      borderRadius: '10px',
      border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
      background: selected ? '#eff6ff' : 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: selected ? '0 2px 8px rgba(59, 130, 246, 0.2)' : 'none'
    }),
    uploadBox: {
      border: '2px dashed #d1d5db',
      borderRadius: '12px',
      padding: isMobile ? '20px' : '30px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#f9fafb'
    },
    uploadPreview: {
      maxWidth: '100%',
      maxHeight: isMobile ? '150px' : '200px',
      borderRadius: '8px',
      marginTop: '10px',
      objectFit: 'contain'
    },
    signatureBox: {
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: isMobile ? '8px' : '10px',
      background: '#f9fafb'
    },
    canvas: {
      width: '100%',
      height: isMobile ? '120px' : '150px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      background: 'white',
      cursor: 'crosshair',
      touchAction: 'none' // Important for mobile touch drawing
    },
    buttonRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      marginTop: isMobile ? '20px' : '30px',
      gap: isMobile ? '10px' : '15px'
    },
    button: (primary) => ({
      padding: isMobile ? '14px 20px' : '12px 30px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      background: primary ? 'linear-gradient(135deg, #1e3a8a, #3b82f6)' : '#e5e7eb',
      color: primary ? 'white' : '#374151',
      transition: 'all 0.2s',
      boxShadow: primary ? '0 2px 8px rgba(30, 58, 138, 0.3)' : 'none',
      width: isMobile ? '100%' : 'auto',
      WebkitTapHighlightColor: 'transparent'
    }),
    engagementText: {
      fontSize: isMobile ? '12px' : '13px',
      lineHeight: '1.7',
      color: '#374151',
      marginBottom: '15px',
      textAlign: 'justify'
    },
    successBox: {
      textAlign: 'center',
      padding: isMobile ? '25px' : '40px',
      background: '#f0fdf4',
      borderRadius: '12px',
      border: '1px solid #22c55e'
    },
    // Additional mobile-friendly styles
    requiredStar: {
      color: '#ef4444',
      marginLeft: '4px'
    },
    helpText: {
      fontSize: isMobile ? '11px' : '12px',
      color: '#6b7280',
      marginTop: '4px'
    },
    errorText: {
      fontSize: isMobile ? '11px' : '12px',
      color: '#ef4444',
      marginTop: '4px'
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    }
  };

  // Add CSS keyframes for spinner animation
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{spinnerStyle}</style>
        <div style={{ 
          textAlign: 'center', 
          padding: isMobile ? '40px 20px' : '50px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginTop: '20px'
        }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid #e5e7eb', 
            borderTop: '4px solid #3b82f6', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite', 
            margin: '0 auto 20px' 
          }}></div>
          <p style={{ color: '#374151', fontSize: isMobile ? '14px' : '16px', margin: 0 }}>Loading form...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.container}>
        <style>{spinnerStyle}</style>
        <div style={styles.header}>
          {company?.landingPageSettings?.logoUrl && (
            <img 
              src={company.landingPageSettings.logoUrl} 
              alt="Company Logo" 
              style={{ ...styles.logo, marginBottom: '10px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h1 style={styles.companyName}>{company?.name || 'Friends Tax Services LLC'}</h1>
          <p style={styles.companyInfo}>{company?.address || '568 Gardenia Drive, Bell Vernon, PA 15012'}</p>
        </div>
        <div style={styles.successBox}>
          <div style={{ fontSize: isMobile ? '50px' : '60px', marginBottom: '20px' }}>⏳</div>
          <h2 style={{ color: '#b45309', marginBottom: '15px', fontSize: isMobile ? '18px' : '22px' }}>Form Submitted - Pending Verification</h2>
          <p style={{ color: '#374151', marginBottom: '10px', fontSize: isMobile ? '13px' : '14px' }}>
            Your IRS Waiver form has been submitted successfully and is now <strong>pending verification</strong> by your tax preparer.
          </p>
          <div style={{ 
            background: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '8px', 
            padding: isMobile ? '12px' : '15px', 
            marginBottom: '20px',
            textAlign: 'left'
          }}>
            <p style={{ margin: '0 0 10px', color: '#92400e', fontWeight: '600', fontSize: isMobile ? '13px' : '14px' }}>📋 What happens next?</p>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: isMobile ? '12px' : '14px', lineHeight: '1.6' }}>
              <li>Your preparer will review your submitted information</li>
              <li>Your photo IDs will be verified for compliance</li>
              <li>Once approved, your documents will be finalized</li>
              <li>You may be contacted if any additional information is needed</li>
            </ul>
          </div>
          <p style={{ color: '#6b7280', fontSize: isMobile ? '12px' : '14px', marginBottom: '20px' }}>
            Reference: {formData.taxpayerFirstName} {formData.taxpayerLastName} - Tax Year {formData.taxYear}
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setCurrentStep('info');
              // Reset form
              setFormData({
                taxYear: getEasternYear(),
                medicaidElection: '',
                careRecipientName: '',
                careRecipientAddress: '',
                taxpayerFirstName: '',
                taxpayerMiddleInitial: '',
                taxpayerLastName: '',
                taxpayerOccupation: '',
                taxpayerEmail: '',
                taxpayerDOB: '',
                taxpayerSSN: '',
                taxpayerPhone: '',
                taxpayerAddress: '',
                taxpayerCity: '',
                taxpayerState: '',
                taxpayerZipCode: '',
                spouseFirstName: '',
                spouseMiddleInitial: '',
                spouseLastName: '',
                spouseOccupation: '',
                spouseEmail: '',
                spouseDOB: '',
                spouseSSN: '',
                spousePhone: '',
                dependents: [
                  { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
                  { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
                  { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' },
                  { firstName: '', middleInitial: '', lastName: '', dob: '', ssn: '', relation: '' }
                ],
                engagementAccepted: false,
                taxpayerSignatureDate: '',
                spouseSignatureDate: ''
              });
              setTaxpayerSignature('');
              setSpouseSignature('');
              setConsumerPhotoPreview(null);
              setCaregiverPhotoPreview(null);
            }}
            style={styles.button(true)}
          >
            Submit Another Form
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{spinnerStyle}</style>
      
      {/* Header with Logo */}
      <div style={styles.header}>
        {company?.landingPageSettings?.logoUrl && (
          <img 
            src={company.landingPageSettings.logoUrl} 
            alt="Company Logo" 
            style={{ ...styles.logo, marginBottom: '10px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <h1 style={styles.companyName}>{company?.name || 'Friends Tax Services LLC'}</h1>
        <p style={styles.companyInfo}>{company?.address || '568 Gardenia Drive, Bell Vernon, PA 15012'}</p>
        <p style={styles.companyInfo}>📞 {company?.phone || '(412) 452-1754'}</p>
        {preparer && (
          <p style={{ ...styles.companyInfo, marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
            Your Tax Preparer: <strong>{preparer.name}</strong>
          </p>
        )}
      </div>

      {/* Progress Bar */}
      <div style={{
        ...styles.progressBar,
        background: 'white',
        padding: isMobile ? '12px 8px' : '15px',
        borderRadius: isMobile ? '8px' : '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: isMobile ? '15px' : '25px'
      }}>
        {steps.map((step, index) => {
          const currentIndex = steps.indexOf(currentStep);
          const isActive = step === currentStep;
          const isCompleted = index < currentIndex;
          return (
            <div key={step} style={styles.progressStep}>
              <div style={styles.progressDot(isActive, isCompleted)}>
                {isCompleted ? '✓' : index + 1}
              </div>
              <div style={styles.progressLabel(isActive)}>
                {isMobile ? (index + 1) : stepLabels[step]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current Step Label - Mobile Only */}
      {isMobile && (
        <div style={{
          textAlign: 'center',
          marginBottom: '15px',
          padding: '10px',
          background: '#eff6ff',
          borderRadius: '8px',
          color: '#1e3a8a',
          fontWeight: '600',
          fontSize: '14px'
        }}>
          Step {steps.indexOf(currentStep) + 1}: {stepLabels[currentStep]}
        </div>
      )}

      {/* Step 1: Client Information */}
      {currentStep === 'info' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📋 Taxpayer Information</h2>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tax Year <span style={styles.requiredStar}>*</span></label>
              <select
                name="taxYear"
                value={formData.taxYear}
                onChange={handleInputChange}
                style={styles.select}
              >
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
                <option value={2024}>2024</option>
              </select>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>First Name <span style={styles.requiredStar}>*</span></label>
              <input
                type="text"
                name="taxpayerFirstName"
                value={formData.taxpayerFirstName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="First name"
                required
                autoComplete="given-name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>M.I.</label>
              <input
                type="text"
                name="taxpayerMiddleInitial"
                value={formData.taxpayerMiddleInitial}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="M"
                maxLength={1}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Last Name *</label>
              <input
                type="text"
                name="taxpayerLastName"
                value={formData.taxpayerLastName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Occupation</label>
              <input
                type="text"
                name="taxpayerOccupation"
                value={formData.taxpayerOccupation}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Occupation"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                name="taxpayerEmail"
                value={formData.taxpayerEmail}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="email@example.com"
                required
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date of Birth *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  name="taxpayerDOB"
                  value={formData.taxpayerDOB}
                  onChange={(e) => handleDOBTextChange('taxpayerDOB', e.target.value)}
                  style={{ ...styles.input, flex: 1 }}
                  placeholder="MM/DD/YYYY"
                  maxLength={10}
                  required
                />
                <input
                  type="date"
                  value={toDateInputFormat(formData.taxpayerDOB)}
                  onChange={(e) => handleDOBDatePickerChange('taxpayerDOB', e.target.value)}
                  style={{ ...styles.input, width: '40px', padding: '8px 4px', cursor: 'pointer' }}
                  title="Use calendar picker"
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>SSN *</label>
              <input
                type="text"
                name="taxpayerSSN"
                value={formData.taxpayerSSN}
                onChange={(e) => setFormData(prev => ({ ...prev, taxpayerSSN: formatSSN(e.target.value) }))}
                style={styles.input}
                placeholder="XXX-XX-XXXX"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone *</label>
              <input
                type="tel"
                name="taxpayerPhone"
                value={formData.taxpayerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, taxpayerPhone: formatPhoneNumber(e.target.value) }))}
                style={styles.input}
                placeholder="XXX-XXX-XXXX"
                required
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Current Address *</label>
            <input
              type="text"
              name="taxpayerAddress"
              value={formData.taxpayerAddress}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Street address"
              required
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>City *</label>
              <input
                type="text"
                name="taxpayerCity"
                value={formData.taxpayerCity}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="City"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>State *</label>
              <input
                type="text"
                name="taxpayerState"
                value={formData.taxpayerState}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="PA"
                maxLength={2}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>ZIP Code *</label>
              <input
                type="text"
                name="taxpayerZipCode"
                value={formData.taxpayerZipCode}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="XXXXX"
                maxLength={10}
                required
              />
            </div>
          </div>

          {/* Spouse Information */}
          <h2 style={{ ...styles.sectionTitle, marginTop: '30px' }}>👫 Spouse Information (if married)</h2>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>First Name</label>
              <input
                type="text"
                name="spouseFirstName"
                value={formData.spouseFirstName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="First name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>M.I.</label>
              <input
                type="text"
                name="spouseMiddleInitial"
                value={formData.spouseMiddleInitial}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="M"
                maxLength={1}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Last Name</label>
              <input
                type="text"
                name="spouseLastName"
                value={formData.spouseLastName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Last name"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Occupation</label>
              <input
                type="text"
                name="spouseOccupation"
                value={formData.spouseOccupation}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Occupation"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="spouseEmail"
                value={formData.spouseEmail}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date of Birth</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  name="spouseDOB"
                  value={formData.spouseDOB}
                  onChange={(e) => handleDOBTextChange('spouseDOB', e.target.value)}
                  style={{ ...styles.input, flex: 1 }}
                  placeholder="MM/DD/YYYY"
                  maxLength={10}
                />
                <input
                  type="date"
                  value={toDateInputFormat(formData.spouseDOB)}
                  onChange={(e) => handleDOBDatePickerChange('spouseDOB', e.target.value)}
                  style={{ ...styles.input, width: '40px', padding: '8px 4px', cursor: 'pointer' }}
                  title="Use calendar picker"
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>SSN</label>
              <input
                type="text"
                name="spouseSSN"
                value={formData.spouseSSN}
                onChange={(e) => setFormData(prev => ({ ...prev, spouseSSN: formatSSN(e.target.value) }))}
                style={styles.input}
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                name="spousePhone"
                value={formData.spousePhone}
                onChange={(e) => setFormData(prev => ({ ...prev, spousePhone: formatPhoneNumber(e.target.value) }))}
                style={styles.input}
                placeholder="XXX-XXX-XXXX"
              />
            </div>
          </div>

          <div style={styles.buttonRow}>
            <div></div>
            <button onClick={nextStep} style={styles.button(true)}>
              Next: Dependents →
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Dependents */}
      {currentStep === 'dependents' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>👨‍👩‍👧‍👦 Dependents Information (if applicable)</h2>
          
          {formData.dependents.map((dep, index) => (
            <div key={index} style={{ marginBottom: '25px', padding: '20px', background: '#f9fafb', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 15px', color: '#1e3a8a', fontSize: '16px' }}>Dependent {index + 1}</h3>
              
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text"
                    value={dep.firstName}
                    onChange={(e) => handleDependentChange(index, 'firstName', e.target.value)}
                    style={styles.input}
                    placeholder="First name"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>M.I.</label>
                  <input
                    type="text"
                    value={dep.middleInitial}
                    onChange={(e) => handleDependentChange(index, 'middleInitial', e.target.value)}
                    style={styles.input}
                    placeholder="M"
                    maxLength={1}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text"
                    value={dep.lastName}
                    onChange={(e) => handleDependentChange(index, 'lastName', e.target.value)}
                    style={styles.input}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date of Birth</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={dep.dob}
                      onChange={(e) => handleDependentDOBTextChange(index, e.target.value)}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="MM/DD/YYYY"
                      maxLength={10}
                    />
                    <input
                      type="date"
                      value={toDateInputFormat(dep.dob)}
                      onChange={(e) => handleDependentDOBDatePickerChange(index, e.target.value)}
                      style={{ ...styles.input, width: '40px', padding: '8px 4px', cursor: 'pointer' }}
                      title="Use calendar picker"
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SSN</label>
                  <input
                    type="text"
                    value={dep.ssn}
                    onChange={(e) => handleDependentChange(index, 'ssn', formatSSN(e.target.value))}
                    style={styles.input}
                    placeholder="XXX-XX-XXXX"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Relation to You</label>
                  <select
                    value={dep.relation}
                    onChange={(e) => handleDependentChange(index, 'relation', e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Select...</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Stepson">Stepson</option>
                    <option value="Stepdaughter">Stepdaughter</option>
                    <option value="Foster Child">Foster Child</option>
                    <option value="Grandchild">Grandchild</option>
                    <option value="Niece">Niece</option>
                    <option value="Nephew">Nephew</option>
                    <option value="Parent">Parent</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          ))}

          <div style={styles.buttonRow}>
            <button onClick={prevStep} style={styles.button(false)}>
              ← Back
            </button>
            <button onClick={nextStep} style={styles.button(true)}>
              Next: IRS Waiver →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: IRS Waiver Election */}
      {currentStep === 'waiver' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📜 IRS Notice 2014-7 Acknowledgment & Election</h2>
          
          <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
              <strong>Important:</strong> Please read the following information carefully before making your election.
            </p>
          </div>

          <div style={styles.engagementText}>
            <p><strong>1. Exclusion of Income:</strong> Medicaid waiver payments I received for providing non-medical care to an eligible individual in my home may be excluded from gross income if I meet the qualifications under IRS Notice 2014-7.</p>
            
            <p><strong>2. Optional Inclusion for Tax Benefits:</strong> Even if my Medicaid waiver payments qualify for exclusion, I may elect to include them as earned income for purposes of certain tax credits, such as the Earned Income Tax Credit (EITC) or Additional Child Tax Credit (ACTC).</p>
            
            <p><strong>3. Tax Filing Responsibility:</strong> It is my responsibility to report my income correctly and consult a tax professional if I need guidance on how IRS Notice 2014-7 applies to my situation.</p>
            
            <p><strong>4. Amendment of Prior Returns:</strong> If I previously reported these payments as taxable income and later determine they qualify for exclusion, I may file an amended return (Form 1040-X) for potential tax adjustments.</p>
          </div>

          <h3 style={{ fontSize: '16px', color: '#1e3a8a', marginBottom: '15px' }}>Election (Choose One) *</h3>
          
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel(formData.medicaidElection === 'exclude')}>
              <input
                type="radio"
                name="medicaidElection"
                value="exclude"
                checked={formData.medicaidElection === 'exclude'}
                onChange={handleInputChange}
                style={{ marginTop: '3px' }}
              />
              <div>
                <strong>EXCLUDE</strong> - I elect to EXCLUDE qualified Medicaid waiver payments from my gross income in accordance with IRS Notice 2014-7.
              </div>
            </label>
            
            <label style={styles.radioLabel(formData.medicaidElection === 'include')}>
              <input
                type="radio"
                name="medicaidElection"
                value="include"
                checked={formData.medicaidElection === 'include'}
                onChange={handleInputChange}
                style={{ marginTop: '3px' }}
              />
              <div>
                <strong>INCLUDE</strong> - I elect to INCLUDE my Medicaid waiver payments as earned income for tax credit eligibility.
              </div>
            </label>
          </div>

          {/* Care Recipient Info for "To Whom It May Concern" */}
          <h3 style={{ fontSize: '16px', color: '#1e3a8a', marginTop: '30px', marginBottom: '15px' }}>Care Recipient Information</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>
            For the "To Whom It May Concern" declaration letter
          </p>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Care Recipient Name *</label>
              <input
                type="text"
                name="careRecipientName"
                value={formData.careRecipientName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Name of person you care for"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Care Recipient Address *</label>
              <input
                type="text"
                name="careRecipientAddress"
                value={formData.careRecipientAddress}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Address"
                required
              />
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button onClick={prevStep} style={styles.button(false)}>
              ← Back
            </button>
            <button onClick={nextStep} style={styles.button(true)} disabled={!formData.medicaidElection}>
              Next: Engagement Letter →
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Engagement Letter */}
      {currentStep === 'engagement' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📝 Engagement Letter for {formData.taxYear - 1} Tax Return</h2>
          
          <div style={{ maxHeight: '400px', overflow: 'auto', padding: '20px', background: '#f9fafb', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={styles.engagementText}><strong>To Our Tax Clients:</strong></p>
            
            <p style={styles.engagementText}>
              The purpose of this engagement letter is to outline your rights and responsibilities that you as a client must follow as well as {company?.name || "Friends Tax Services LLC"}'s participation and obligation to you, our valued client. On signing this letter, you acknowledge the following statements and agree to the following procedures with our firm to the best of your knowledge and understanding.
            </p>
            
            <p style={styles.engagementText}>
              We will prepare your {formData.taxYear - 1} federal, state individual or local income tax returns using information you provide to us. We may ask for clarification of some items; it is your responsibility to provide information required for preparation of complete and accurate returns. You should keep all documents, canceled checks and other data that support your reported income and deductions. They may be necessary to prove accuracy and completeness of the returns to a taxing authority. You are responsible for the returns, so you should review them carefully before you sign them.
            </p>
            
            <p style={styles.engagementText}>
              Our work will not include any procedures to discover faults or other irregularities. The only accounting or analysis work we will do is that which is necessary for preparation of your income tax returns. We must use our judgment in resolving questions where the tax law is unclear, or where there may be conflicts between the taxing authorities' interpretations of the law and other supportable positions. To avoid penalties, we will apply the "more likely than not" reliance standard to resolve such issues. You agree to honor our decisions regarding the need to make protective disclosures in your returns.
            </p>
            
            <p style={styles.engagementText}>
              Penalties of as much as $100,000 can be imposed on you for failing to disclose participation in "reportable transactions," that is, certain arrangements the IRS has identified as potentially abusive. We will insist that all such transactions be properly disclosed. The law also imposes penalties when taxpayers understate their tax liability. If you have concerns about such penalties, please contact a tax attorney.
            </p>
            
            <p style={styles.engagementText}>
              Taxpayers are required to maintain all the documents that form the basis of income, deductions, credits and payments shown on the return. In addition, some items have specific substantiation requirements set forth by the IRS (e.g., auto, meals & entertainment, and charitable contributions over $250, etc.). If you have questions as to the type of records required, please ask us for advice in that regard. Your signature on this letter confirms that we have advised you of the record keeping requirements.
            </p>
            
            <p style={styles.engagementText}>
              Your returns will be required to be filed electronically with the IRS. We will provide you with a copy of your final returns for review prior to electronic transmission. The IRS requires that you sign an e-file authorization form indicating that you have reviewed the return, it is correct to the best of your knowledge, and you authorize us to submit it electronically. We cannot transmit any return until we have the appropriate signed authorizations.
            </p>
            
            <p style={styles.engagementText}>
              Our fee for preparation of your tax returns will be based on the amount of time required at standard billing rates plus out-of-pocket expenses. All invoices are due and payable upon presentation. To the extent permitted by state law, an interest charge may be added to all accounts not paid within thirty (30) days.
            </p>
            
            <p style={styles.engagementText}>
              We will retain copies of records you supplied to us along with our work papers for your engagement for a period of 7 years. After 7 years, our work papers and engagement files will be destroyed. All your original records will be returned to you at the end of this engagement.
            </p>
            
            <p style={styles.engagementText}>
              We appreciate your confidence in us. Please call if you have questions.
            </p>
            
            <p style={styles.engagementText}>
              Sincerely,<br/>
              <strong>{company?.name || 'Friends Tax Services LLC'}</strong>
            </p>
          </div>

          {/* Single Accept Checkbox - Required to Continue */}
          <div style={{ 
            background: '#f0fdf4', 
            border: '2px solid #22c55e', 
            borderRadius: '12px', 
            padding: '20px', 
            marginBottom: '20px' 
          }}>
            <label style={{ ...styles.checkboxLabel, marginBottom: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="engagementAccepted"
                checked={formData.engagementAccepted}
                onChange={handleInputChange}
                style={{ ...styles.checkbox, width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#166534' }}>
                ✓ I ACCEPT - I have read, understand, and agree to all the terms and conditions outlined in the engagement letter above.
              </span>
            </label>
          </div>

          {!formData.engagementAccepted && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '14px' }}>
                ⚠️ You must accept the engagement letter terms to continue with the form submission.
              </p>
            </div>
          )}

          <div style={styles.buttonRow}>
            <button onClick={prevStep} style={styles.button(false)}>
              ← Back
            </button>
            <button 
              onClick={nextStep} 
              style={styles.button(formData.engagementAccepted)} 
              disabled={!formData.engagementAccepted}
            >
              {formData.engagementAccepted ? 'Next: Photo ID Upload →' : '🔒 Accept Terms to Continue'}
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Photo ID Upload */}
      {currentStep === 'photos' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📷 Photo ID Verification</h2>
          
          <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
              <strong>Note:</strong> Please upload valid government-issued photo identification for both the Consumer and the Caregiver. Both IDs must show the same current address for verification purposes.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {/* Consumer Photo ID */}
            <div>
              <h3 style={{ fontSize: '16px', color: '#1e3a8a', marginBottom: '15px' }}>Consumer Photo ID Upload *</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                (Accepted formats: JPG, PNG, PDF)
              </p>
              <div 
                style={styles.uploadBox}
                onClick={() => document.getElementById('consumerPhotoInput').click()}
              >
                {consumerPhotoPreview ? (
                  <div>
                    <img src={consumerPhotoPreview} alt="Consumer ID" style={styles.uploadPreview} />
                    <p style={{ marginTop: '10px', color: '#22c55e' }}>✓ Photo uploaded</p>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>📤</div>
                    <p style={{ color: '#6b7280' }}>Click to upload Consumer Photo ID</p>
                  </div>
                )}
              </div>
              <input
                id="consumerPhotoInput"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handlePhotoUpload(e, 'consumer')}
                style={{ display: 'none' }}
              />
            </div>

            {/* Caregiver Photo ID */}
            <div>
              <h3 style={{ fontSize: '16px', color: '#1e3a8a', marginBottom: '15px' }}>Caregiver Photo ID Upload *</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                (Accepted formats: JPG, PNG, PDF)
              </p>
              <div 
                style={styles.uploadBox}
                onClick={() => document.getElementById('caregiverPhotoInput').click()}
              >
                {caregiverPhotoPreview ? (
                  <div>
                    <img src={caregiverPhotoPreview} alt="Caregiver ID" style={styles.uploadPreview} />
                    <p style={{ marginTop: '10px', color: '#22c55e' }}>✓ Photo uploaded</p>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>📤</div>
                    <p style={{ color: '#6b7280' }}>Click to upload Caregiver Photo ID</p>
                  </div>
                )}
              </div>
              <input
                id="caregiverPhotoInput"
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handlePhotoUpload(e, 'caregiver')}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '20px', fontStyle: 'italic' }}>
            Note: Ensure that the address on both IDs matches exactly. Incomplete or unclear documents may delay processing.
          </p>

          <div style={styles.buttonRow}>
            <button onClick={prevStep} style={styles.button(false)}>
              ← Back
            </button>
            <button 
              onClick={nextStep} 
              style={styles.button(true)}
              disabled={!consumerPhotoPreview || !caregiverPhotoPreview}
            >
              Next: Review & Sign →
            </button>
          </div>
        </div>
      )}

      {/* Step 6: Review & Sign */}
      {currentStep === 'review' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>✍️ Review & Sign</h2>
          
          {/* Summary */}
          <div style={{ background: '#f0f9ff', padding: '20px', borderRadius: '8px', marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 15px', color: '#1e3a8a' }}>Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', fontSize: '14px' }}>
              <div>
                <strong>Taxpayer:</strong><br/>
                {formData.taxpayerFirstName} {formData.taxpayerMiddleInitial} {formData.taxpayerLastName}
              </div>
              <div>
                <strong>Email:</strong><br/>
                {formData.taxpayerEmail}
              </div>
              <div>
                <strong>Phone:</strong><br/>
                {formData.taxpayerPhone}
              </div>
              <div>
                <strong>Tax Year:</strong><br/>
                {formData.taxYear}
              </div>
              <div>
                <strong>Medicaid Election:</strong><br/>
                {formData.medicaidElection === 'exclude' ? 'EXCLUDE from income' : 'INCLUDE as earned income'}
              </div>
              <div>
                <strong>Care Recipient:</strong><br/>
                {formData.careRecipientName}
              </div>
            </div>
          </div>

          {/* Certification */}
          <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: '20px', borderRadius: '12px', marginBottom: '25px', border: '1px solid #f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <p style={{ margin: 0, fontSize: '14px', color: '#92400e', lineHeight: '1.6' }}>
                I certify that the information provided is true and accurate to the best of my knowledge. I understand that this election may impact my tax return and agree to retain supporting documentation for tax purposes.
              </p>
            </div>
          </div>

          {/* Taxpayer Signature */}
          <div style={{ 
            marginBottom: '30px', 
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
            padding: isMobile ? '20px' : '25px', 
            borderRadius: '16px',
            border: '2px solid #0ea5e9'
          }}>
            <h3 style={{ 
              fontSize: isMobile ? '18px' : '20px', 
              color: '#0c4a6e', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ 
                background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', 
                color: 'white', 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '18px'
              }}>✍️</span>
              Taxpayer Signature <span style={{ color: '#dc2626' }}>*</span>
            </h3>
            
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
              Choose how you'd like to sign. For the best experience on mobile, use "Sign on Phone" to sign with your finger.
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
              gap: '12px',
              marginBottom: '20px'
            }}>
              <button 
                onClick={() => { setActiveSignature('taxpayer'); setSignatureType('draw'); }}
                style={{ 
                  padding: '16px 20px', 
                  border: signatureType === 'draw' && activeSignature === 'taxpayer' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                  borderRadius: '12px',
                  background: signatureType === 'draw' && activeSignature === 'taxpayer' ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: signatureType === 'draw' && activeSignature === 'taxpayer' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                <span style={{ fontSize: '28px' }}>✏️</span>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Draw</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Use mouse/touch</span>
              </button>
              <button 
                onClick={() => { setActiveSignature('taxpayer'); setSignatureType('type'); }}
                style={{ 
                  padding: '16px 20px', 
                  border: signatureType === 'type' && activeSignature === 'taxpayer' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                  borderRadius: '12px',
                  background: signatureType === 'type' && activeSignature === 'taxpayer' ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: signatureType === 'type' && activeSignature === 'taxpayer' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                <span style={{ fontSize: '28px' }}>⌨️</span>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Type</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Type your name</span>
              </button>
              <button 
                onClick={() => generateQRCode('taxpayer')}
                disabled={qrLoading}
                style={{ 
                  padding: '16px 20px', 
                  border: '2px solid #8b5cf6',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  color: 'white',
                  cursor: qrLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                }}
              >
                <span style={{ fontSize: '28px' }}>📱</span>
                <span style={{ fontWeight: '700' }}>{qrLoading ? 'Generating...' : 'Sign on Phone'}</span>
                <span style={{ fontSize: '12px', opacity: 0.9 }}>Best for mobile!</span>
              </button>
            </div>

            {signatureType === 'draw' && activeSignature === 'taxpayer' && (
              <div style={{ 
                background: 'white', 
                borderRadius: '12px', 
                padding: '15px',
                border: '2px solid #e2e8f0'
              }}>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px', textAlign: 'center' }}>
                  👆 Draw your signature below using mouse or finger
                </p>
                <div style={{ 
                  touchAction: 'none', 
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    style={{ 
                      ...styles.canvas, 
                      border: '2px dashed #cbd5e1', 
                      background: '#fafafa',
                      touchAction: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      WebkitTouchCallout: 'none'
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(e) => { e.preventDefault(); startDrawing(e); }}
                    onTouchMove={(e) => { e.preventDefault(); draw(e); }}
                    onTouchEnd={(e) => { e.preventDefault(); stopDrawing(); }}
                  />
                </div>
                <button onClick={clearSignature} style={{ 
                  marginTop: '12px', 
                  padding: '10px 20px', 
                  background: 'linear-gradient(135deg, #fee2e2, #fecaca)', 
                  border: '1px solid #fca5a5',
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  🗑️ Clear Signature
                </button>
              </div>
            )}

            {signatureType === 'type' && activeSignature === 'taxpayer' && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '15px', border: '2px solid #e2e8f0' }}>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>
                  Type your full legal name below:
                </p>
                <input
                  type="text"
                  value={taxpayerSignature}
                  onChange={(e) => setTaxpayerSignature(e.target.value)}
                  placeholder="Type your full name"
                  style={{ 
                    ...styles.input, 
                    fontFamily: "'Brush Script MT', 'Segoe Script', cursive", 
                    fontSize: isMobile ? '28px' : '32px',
                    padding: '15px',
                    textAlign: 'center',
                    color: '#1e3a8a',
                    border: '2px dashed #cbd5e1',
                    background: '#fafafa'
                  }}
                />
              </div>
            )}

            {/* Pending Taxpayer Signature - Needs Accept/Decline */}
            {pendingTaxpayerSignature && !taxpayerSignature && (
              <div style={{ 
                marginTop: '15px',
                padding: '20px',
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                borderRadius: '12px',
                border: '2px solid #f59e0b',
                animation: 'pulse 2s infinite'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '15px'
                }}>
                  <span style={{ fontSize: '24px' }}>⏳</span>
                  <span style={{ color: '#92400e', fontWeight: '700', fontSize: '16px' }}>Signature Received - Please Review</span>
                </div>
                
                <div style={{ 
                  background: 'white', 
                  padding: '15px', 
                  borderRadius: '10px',
                  border: '2px solid #fbbf24',
                  textAlign: 'center',
                  marginBottom: '15px'
                }}>
                  <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>Taxpayer Signature Preview:</p>
                  <img 
                    src={pendingTaxpayerSignature} 
                    alt="Pending Taxpayer Signature" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '120px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: 'white'
                    }} 
                  />
                </div>
                
                <p style={{ fontSize: '13px', color: '#78350f', marginBottom: '15px', textAlign: 'center' }}>
                  Does this signature look correct? Accept to use it in the form, or Decline to sign again.
                </p>
                
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => acceptPendingSignature('taxpayer')}
                    style={{
                      padding: '12px 30px',
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '700',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ✅ Accept Signature
                  </button>
                  <button 
                    onClick={() => declinePendingSignature('taxpayer')}
                    style={{
                      padding: '12px 30px',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '700',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    ❌ Decline & Re-sign
                  </button>
                </div>
              </div>
            )}

            {taxpayerSignature && (
              <div style={{ 
                marginTop: '15px',
                padding: '16px',
                background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                borderRadius: '12px',
                border: '2px solid #86efac'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: taxpayerSignature.startsWith('data:image') ? '12px' : '0'
                }}>
                  <span style={{ fontSize: '20px' }}>✅</span>
                  <span style={{ color: '#166534', fontWeight: '600' }}>Taxpayer signature captured successfully!</span>
                </div>
                
                {/* Show signature preview if it's an image (from QR code or canvas) */}
                {taxpayerSignature.startsWith('data:image') && (
                  <div style={{ 
                    background: 'white', 
                    padding: '12px', 
                    borderRadius: '8px',
                    border: '1px solid #86efac',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Signature Preview:</p>
                    <img 
                      src={taxpayerSignature} 
                      alt="Taxpayer Signature" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        background: 'white'
                      }} 
                    />
                    <button 
                      onClick={() => setTaxpayerSignature('')}
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fca5a5',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      🗑️ Clear & Re-sign
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '15px' }}>
              <label style={styles.label}>Date</label>
              <input
                type="date"
                name="taxpayerSignatureDate"
                value={formData.taxpayerSignatureDate || toEasternInputDate()}
                onChange={handleInputChange}
                style={{ ...styles.input, maxWidth: '200px' }}
              />
            </div>
          </div>

          {/* Spouse Signature (optional) */}
          {(formData.spouseFirstName || formData.spouseLastName) && (
            <div style={{ 
              marginBottom: '30px', 
              background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)', 
              padding: isMobile ? '20px' : '25px', 
              borderRadius: '16px',
              border: '2px solid #d946ef'
            }}>
              <h3 style={{ 
                fontSize: isMobile ? '18px' : '20px', 
                color: '#86198f', 
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ 
                  background: 'linear-gradient(135deg, #d946ef, #a855f7)', 
                  color: 'white', 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>👥</span>
                Spouse Signature
                <span style={{ 
                  fontSize: '12px', 
                  background: '#f3e8ff', 
                  padding: '4px 10px', 
                  borderRadius: '20px', 
                  color: '#7c3aed',
                  fontWeight: '500'
                }}>Optional</span>
              </h3>
              
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                If filing jointly, your spouse can sign here. Use "Sign on Phone" so they can sign on their own device.
              </p>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
                gap: '12px',
                marginBottom: '20px'
              }}>
                <button 
                  onClick={() => { setActiveSignature('spouse'); setSignatureType('draw'); }}
                  style={{ 
                    padding: '16px 20px', 
                    border: signatureType === 'draw' && activeSignature === 'spouse' ? '2px solid #d946ef' : '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: signatureType === 'draw' && activeSignature === 'spouse' ? 'linear-gradient(135deg, #fdf4ff, #fae8ff)' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: signatureType === 'draw' && activeSignature === 'spouse' ? '0 4px 12px rgba(217, 70, 239, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <span style={{ fontSize: '28px' }}>✏️</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Draw</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Use mouse/touch</span>
                </button>
                <button 
                  onClick={() => { setActiveSignature('spouse'); setSignatureType('type'); }}
                  style={{ 
                    padding: '16px 20px', 
                    border: signatureType === 'type' && activeSignature === 'spouse' ? '2px solid #d946ef' : '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: signatureType === 'type' && activeSignature === 'spouse' ? 'linear-gradient(135deg, #fdf4ff, #fae8ff)' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: signatureType === 'type' && activeSignature === 'spouse' ? '0 4px 12px rgba(217, 70, 239, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <span style={{ fontSize: '28px' }}>⌨️</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>Type</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Type spouse name</span>
                </button>
                <button 
                  onClick={() => generateQRCode('spouse')}
                  disabled={qrLoading}
                  style={{ 
                    padding: '16px 20px', 
                    border: '2px solid #d946ef',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                    color: 'white',
                    cursor: qrLoading ? 'wait' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 15px rgba(217, 70, 239, 0.4)'
                  }}
                >
                  <span style={{ fontSize: '28px' }}>📱</span>
                  <span style={{ fontWeight: '700' }}>{qrLoading ? 'Generating...' : 'Sign on Phone'}</span>
                  <span style={{ fontSize: '12px', opacity: 0.9 }}>Share with spouse!</span>
                </button>
              </div>

              {signatureType === 'draw' && activeSignature === 'spouse' && (
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '15px',
                  border: '2px solid #e2e8f0'
                }}>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px', textAlign: 'center' }}>
                    👆 Spouse draws signature below using mouse or finger
                  </p>
                  <div style={{ 
                    touchAction: 'none', 
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <canvas
                      ref={spouseCanvasRef}
                      width={500}
                      height={150}
                      style={{ 
                        ...styles.canvas, 
                        border: '2px dashed #e9d5ff', 
                        background: '#fdfaff',
                        touchAction: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTouchCallout: 'none'
                      }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={(e) => { e.preventDefault(); startDrawing(e); }}
                      onTouchMove={(e) => { e.preventDefault(); draw(e); }}
                      onTouchEnd={(e) => { e.preventDefault(); stopDrawing(); }}
                    />
                  </div>
                  <button onClick={() => clearSignature('spouse')} style={{ 
                    marginTop: '12px', 
                    padding: '10px 20px', 
                    background: 'linear-gradient(135deg, #fee2e2, #fecaca)', 
                    border: '1px solid #fca5a5',
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    🗑️ Clear Signature
                  </button>
                </div>
              )}

              {signatureType === 'type' && activeSignature === 'spouse' && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '15px', border: '2px solid #e2e8f0' }}>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>
                    Type spouse's full legal name below:
                  </p>
                  <input
                    type="text"
                    value={spouseSignature}
                    onChange={(e) => setSpouseSignature(e.target.value)}
                    placeholder="Type spouse's full name"
                    style={{ 
                      ...styles.input, 
                      fontFamily: "'Brush Script MT', 'Segoe Script', cursive", 
                      fontSize: isMobile ? '28px' : '32px',
                      padding: '15px',
                      textAlign: 'center',
                      color: '#86198f',
                      border: '2px dashed #e9d5ff',
                      background: '#fdfaff'
                    }}
                  />
                </div>
              )}

              {/* Pending Spouse Signature - Needs Accept/Decline */}
              {pendingSpouseSignature && !spouseSignature && (
                <div style={{ 
                  marginTop: '15px',
                  padding: '20px',
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  borderRadius: '12px',
                  border: '2px solid #f59e0b',
                  animation: 'pulse 2s infinite'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    marginBottom: '15px'
                  }}>
                    <span style={{ fontSize: '24px' }}>⏳</span>
                    <span style={{ color: '#92400e', fontWeight: '700', fontSize: '16px' }}>Spouse Signature Received - Please Review</span>
                  </div>
                  
                  <div style={{ 
                    background: 'white', 
                    padding: '15px', 
                    borderRadius: '10px',
                    border: '2px solid #fbbf24',
                    textAlign: 'center',
                    marginBottom: '15px'
                  }}>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>Spouse Signature Preview:</p>
                    <img 
                      src={pendingSpouseSignature} 
                      alt="Pending Spouse Signature" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '120px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        background: 'white'
                      }} 
                    />
                  </div>
                  
                  <p style={{ fontSize: '13px', color: '#78350f', marginBottom: '15px', textAlign: 'center' }}>
                    Does this signature look correct? Accept to use it in the form, or Decline to sign again.
                  </p>
                  
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => acceptPendingSignature('spouse')}
                      style={{
                        padding: '12px 30px',
                        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      ✅ Accept Signature
                    </button>
                    <button 
                      onClick={() => declinePendingSignature('spouse')}
                      style={{
                        padding: '12px 30px',
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      ❌ Decline & Re-sign
                    </button>
                  </div>
                </div>
              )}

              {spouseSignature && (
                <div style={{ 
                  marginTop: '15px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                  borderRadius: '12px',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: spouseSignature.startsWith('data:image') ? '12px' : '0'
                  }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <span style={{ color: '#166534', fontWeight: '600' }}>Spouse signature captured successfully!</span>
                  </div>
                  
                  {/* Show signature preview if it's an image (from QR code or canvas) */}
                  {spouseSignature.startsWith('data:image') && (
                    <div style={{ 
                      background: 'white', 
                      padding: '12px', 
                      borderRadius: '8px',
                      border: '1px solid #86efac',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Signature Preview:</p>
                      <img 
                        src={spouseSignature} 
                        alt="Spouse Signature" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          background: 'white'
                        }} 
                      />
                      <button 
                        onClick={() => setSpouseSignature('')}
                        style={{
                          marginTop: '10px',
                          padding: '8px 16px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        🗑️ Clear & Re-sign
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: '15px' }}>
                <label style={styles.label}>Date</label>
                <input
                  type="date"
                  name="spouseSignatureDate"
                  value={formData.spouseSignatureDate || toEasternInputDate()}
                  onChange={handleInputChange}
                  style={{ ...styles.input, maxWidth: '200px' }}
                />
              </div>
            </div>
          )}

          <div style={styles.buttonRow}>
            <button onClick={prevStep} style={styles.button(false)}>
              ← Back
            </button>
            <button 
              onClick={handleSubmit} 
              style={{ ...styles.button(true), background: '#22c55e' }}
              disabled={submitting || !taxpayerSignature}
            >
              {submitting ? 'Generating PDF...' : '✓ Submit & Download PDF'}
            </button>
          </div>
        </div>
      )}

      {/* QR Code Modal for Mobile Signing */}
      {showQRModal && (qrSessionId || qrReceivedSignature) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '24px',
            padding: isMobile ? '25px 20px' : '35px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
            position: 'relative',
            border: '1px solid rgba(255,255,255,0.2)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <button
              onClick={closeQRModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#64748b',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              ✕
            </button>
            
            {/* Show signature received view */}
            {qrReceivedSignature ? (
              <>
                <div style={{
                  width: '90px',
                  height: '90px',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '42px',
                  boxShadow: '0 8px 25px rgba(34, 197, 94, 0.4)',
                  animation: 'bounceIn 0.5s ease'
                }}>
                  ✅
                </div>
                
                <h2 style={{ 
                  color: '#166534', 
                  marginBottom: '8px', 
                  fontSize: isMobile ? '1.4rem' : '1.6rem',
                  fontWeight: '700'
                }}>
                  Signature Received!
                </h2>
                
                <p style={{ 
                  color: '#64748b', 
                  marginBottom: '20px', 
                  fontSize: '1rem',
                  lineHeight: '1.5'
                }}>
                  {qrSignatureType === 'spouse' ? 'Spouse' : 'Taxpayer'} has signed on their phone. Please review the signature below.
                </p>
                
                {/* Signature Preview Box */}
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  padding: '20px',
                  borderRadius: '16px',
                  border: '3px solid #f59e0b',
                  marginBottom: '20px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    marginBottom: '15px'
                  }}>
                    <span style={{ fontSize: '24px' }}>✍️</span>
                    <span style={{ color: '#92400e', fontWeight: '700', fontSize: '16px' }}>
                      {qrSignatureType === 'spouse' ? 'Spouse' : 'Taxpayer'} Signature Preview
                    </span>
                  </div>
                  
                  <div style={{ 
                    background: 'white', 
                    padding: '20px', 
                    borderRadius: '12px',
                    border: '2px solid #fbbf24',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}>
                    <img 
                      src={qrReceivedSignature} 
                      alt="Signature from phone" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '140px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: 'white'
                      }} 
                    />
                  </div>
                </div>
                
                <p style={{ 
                  fontSize: '14px', 
                  color: '#78350f', 
                  marginBottom: '20px', 
                  background: '#fef3c7',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #fde68a'
                }}>
                  ⚠️ Does this signature look correct? <strong>Accept</strong> to use it in the form, or <strong>Decline</strong> to have them sign again.
                </p>
                
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button 
                    onClick={acceptQrSignature}
                    style={{
                      padding: '16px 40px',
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '700',
                      boxShadow: '0 6px 20px rgba(34, 197, 94, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ✅ Accept Signature
                  </button>
                  <button 
                    onClick={declineQrSignature}
                    style={{
                      padding: '16px 40px',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '700',
                      boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ❌ Decline & Re-sign
                  </button>
                </div>
              </>
            ) : (
              /* Show QR code waiting view */
              <>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: qrSignatureType === 'spouse' 
                    ? 'linear-gradient(135deg, #d946ef, #a855f7)'
                    : 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '36px',
                  boxShadow: qrSignatureType === 'spouse'
                    ? '0 8px 25px rgba(217, 70, 239, 0.4)'
                    : '0 8px 25px rgba(139, 92, 246, 0.4)'
                }}>
                  📱
                </div>
                
                <h2 style={{ 
                  color: '#0f172a', 
                  marginBottom: '8px', 
                  fontSize: isMobile ? '1.3rem' : '1.5rem',
                  fontWeight: '700'
                }}>
                  {qrSignatureType === 'spouse' ? 'Spouse Sign on Phone' : 'Sign with Your Phone'}
                </h2>
                
                <p style={{ 
                  color: '#64748b', 
                  marginBottom: '25px', 
                  fontSize: '0.95rem',
                  lineHeight: '1.5'
                }}>
                  {qrSignatureType === 'spouse' 
                    ? 'Have your spouse scan this QR code to sign on their phone.'
                    : 'Scan this QR code with your phone camera to sign with your finger.'
                  }
                </p>
                
                <div style={{
                  background: 'white',
                  padding: '25px',
                  borderRadius: '20px',
                  border: '3px solid',
                  borderColor: qrSignatureType === 'spouse' ? '#d946ef' : '#8b5cf6',
                  display: 'inline-block',
                  marginBottom: '20px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }}>
                  <QRCodeSVG 
                    value={`${window.location.origin}/mobile-sign?session=${qrSessionId}`}
                    size={isMobile ? 180 : 220}
                    level="H"
                    includeMargin={false}
                    fgColor={qrSignatureType === 'spouse' ? '#86198f' : '#4c1d95'}
                  />
                </div>
                
                <div style={{
                  background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                  border: '1px solid #6ee7b7',
                  borderRadius: '14px',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: '#22c55e',
                    borderRadius: '50%',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}></div>
                  <p style={{ 
                    color: '#166534', 
                    fontSize: '0.95rem', 
                    margin: 0,
                    fontWeight: '600'
                  }}>
                    Waiting for {qrSignatureType === 'taxpayer' ? 'taxpayer' : 'spouse'} signature...
                  </p>
                </div>
                
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#22c55e',
                    animation: 'pulse 1s ease-in-out infinite'
                  }}></div>
                  <p style={{ 
                    color: '#64748b', 
                    fontSize: '0.85rem',
                    margin: 0
                  }}>
                    Auto-checking for signature every 1.5 seconds...
                  </p>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={closeQRModal}
                    style={{
                      padding: '14px 30px',
                      background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                      border: '1px solid #cbd5e1',
                      borderRadius: '12px',
                      color: '#475569',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={manualCheckSignature}
                    style={{
                      padding: '10px 16px',
                      background: 'transparent',
                      border: '1px solid #94a3b8',
                      borderRadius: '8px',
                      color: '#64748b',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🔍 Check Manually
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default IRSWaiverIntakeForm;
